import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Lock,
  Unlock,
  Building2,
  DollarSign,
  Calculator,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  Shield,
  Eye,
  EyeOff,
  Pencil,
  Clock,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/Profit.css";

const PROFIT_PASSWORD = "1996";

const Profit = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [customers, setCustomers] = useState([]);
  const [customerData, setCustomerData] = useState({}); // {customer_id: {regular_rate, weekend_rate}}
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("sites"); // 'sites' or 'customers'
  const [customerLevelData, setCustomerLevelData] = useState({}); // For per-customer input
  const [presenceHours, setPresenceHours] = useState({}); // Store regular/weekend hours
  const [systemCosts, setSystemCosts] = useState({}); // Store salaries and enforcement from system
  const [savedProfitData, setSavedProfitData] = useState({}); // Saved profit data from DB

  // בדיקה mode states
  const [isBedika, setIsBedika] = useState(null); // null=not chosen, true/false
  const [bedikaLookup, setBedikaLookup] = useState({}); // name/num -> employment cost
  const [bedikaRows, setBedikaRows] = useState([]); // [{name, num, value}] for fuzzy match
  const [bedikaFileUploaded, setBedikaFileUploaded] = useState(false);
  const [bedikaFileInfo, setBedikaFileInfo] = useState(null); // {col, colLetter, count, headers}
  const [bedikaSourceWb, setBedikaSourceWb] = useState(null); // original uploaded workbook
  const [employeeHours, setEmployeeHours] = useState({}); // customer_id -> [employees]
  const [socialFactors, setSocialFactors] = useState({}); // employee_id -> per-emp override
  const [costOverrides, setCostOverrides] = useState({}); // employee_id -> manual עלות העסקה
  const [globalSocialFactors, setGlobalSocialFactors] = useState({
    chevra_israeli: "1.11",
    chevra_foreign: "1.27",
    kablan: "1.00",
  });
  const [filters, setFilters] = useState({
    name: "",
    type: "",
    israeli: "",
    site: "",
  });
  const [bedikaLoading, setBedikaLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Get unique customer names
  const uniqueCustomerNames = [...new Set(customers.map((c) => c.name))].sort();

  // Group customers by name (for customers view)
  const customersByName = customers.reduce((acc, c) => {
    if (!acc[c.name]) acc[c.name] = [];
    acc[c.name].push(c);
    return acc;
  }, {});

  useEffect(() => {
    // Set default month to current month
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, "0")}`;
    setSelectedMonth(currentMonth);
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      loadCustomers();
    }
  }, [isUnlocked]);

  useEffect(() => {
    if (isUnlocked && selectedMonth && customers.length > 0) {
      loadSavedData();
    }
  }, [isUnlocked, selectedMonth, customers.length]);

  useEffect(() => {
    if (isBedika && isUnlocked && selectedMonth && customers.length > 0) {
      loadEmployeeHours();
    }
  }, [isBedika, isUnlocked, selectedMonth, customers.length]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === PROFIT_PASSWORD) {
      setIsUnlocked(true);
      setPasswordError("");
    } else {
      setPasswordError("סיסמה שגויה");
    }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProfitCustomers();
      if (response.success) {
        setCustomers(response.customers || []);
        // Initialize customerData with rates
        const initialData = {};
        response.customers.forEach((c) => {
          initialData[c.id] = { regular_rate: "", weekend_rate: "" };
        });
        setCustomerData(initialData);

        // Initialize customerLevelData (per customer name)
        const customerNames = [
          ...new Set(response.customers.map((c) => c.name)),
        ];
        const initialCustomerData = {};
        customerNames.forEach((name) => {
          initialCustomerData[name] = { regular_rate: "", weekend_rate: "" };
        });
        setCustomerLevelData(initialCustomerData);
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedData = async () => {
    try {
      // Fetch saved profit data (for rates) and presence hours
      const [profitResponse, presenceResponse] = await Promise.all([
        apiService.getProfitData(selectedMonth),
        apiService.getPresenceHours(selectedMonth),
      ]);

      const savedData = profitResponse.success ? profitResponse.data : {};
      const presenceData = presenceResponse.success
        ? presenceResponse.data
        : {};

      // Store saved profit data
      setSavedProfitData(savedData);

      // Store presence hours with regular/weekend breakdown
      setPresenceHours(presenceData);

      // Fetch system costs (salaries and enforcement) for each customer
      const costsPromises = customers.map((c) =>
        apiService.getCustomerCosts(c.id, selectedMonth).catch(() => ({
          success: false,
          data: { salaries: 0, enforcement: 0 },
        })),
      );
      const costsResults = await Promise.all(costsPromises);

      const costsData = {};
      customers.forEach((c, index) => {
        const result = costsResults[index];
        costsData[c.id] = result.success
          ? result.data
          : { salaries: 0, enforcement: 0 };
      });
      setSystemCosts(costsData);

      // Update customerData with saved rates
      setCustomerData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((customerId) => {
          const saved = savedData[customerId] || {};
          updated[customerId] = {
            regular_rate: saved.regular_rate || "",
            weekend_rate: saved.weekend_rate || "",
          };
        });
        return updated;
      });

      // Update customerLevelData for customers view
      const customerGroups = {};
      customers.forEach((c) => {
        if (!customerGroups[c.name]) customerGroups[c.name] = [];
        customerGroups[c.name].push(c.id);
      });

      setCustomerLevelData((prev) => {
        const updated = { ...prev };
        Object.entries(customerGroups).forEach(([name, ids]) => {
          const firstId = ids[0];
          const saved = savedData[firstId] || {};
          updated[name] = {
            regular_rate: saved.regular_rate || "",
            weekend_rate: saved.weekend_rate || "",
          };
        });
        return updated;
      });
    } catch (error) {
      console.error("Failed to load saved data:", error);
    }
  };

  const handleInputChange = (customerId, field, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [customerId]: {
        ...prev[customerId],
        [field]: value,
      },
    }));
  };

  const handleCustomerLevelChange = (customerName, field, value) => {
    setCustomerLevelData((prev) => ({
      ...prev,
      [customerName]: {
        ...prev[customerName],
        [field]: value,
      },
    }));

    // Also update all sites under this customer
    const sitesForCustomer = customers.filter((c) => c.name === customerName);
    sitesForCustomer.forEach((site) => {
      setCustomerData((prev) => ({
        ...prev,
        [site.id]: {
          ...prev[site.id],
          [field]: value,
        },
      }));
    });
  };

  const saveAllProfitData = async () => {
    try {
      setSaving(true);

      // Prepare data for all customers with rates
      const customersToSave = Object.entries(customerData)
        .filter(([_, data]) => data.regular_rate || data.weekend_rate)
        .map(([customerId, data]) => ({
          customer_id: parseInt(customerId),
          regular_rate: parseFloat(data.regular_rate) || 0,
          weekend_rate: parseFloat(data.weekend_rate) || 0,
        }));

      if (customersToSave.length === 0) {
        alert("אנא הזן תעריף לפחות ללקוח אחד");
        return;
      }

      // Save data to database
      const response = await apiService.saveProfitData(
        selectedMonth,
        customersToSave,
      );

      if (response.success) {
        alert(`נשמרו ${response.saved_count} רשומות בהצלחה!`);
        // Reload data to show updated values
        await loadSavedData();
      }
    } catch (error) {
      console.error("Failed to save profit data:", error);
      alert("שגיאה בשמירת הנתונים");
    } finally {
      setSaving(false);
    }
  };

  // ───────────── בדיקה helpers ─────────────

  const loadEmployeeHours = async () => {
    try {
      setBedikaLoading(true);
      const res = await apiService.getEmployeeHours(selectedMonth);
      if (res.success) setEmployeeHours(res.data);
    } catch (e) {
      console.error("Failed to load employee hours:", e);
    } finally {
      setBedikaLoading(false);
    }
  };

  const parseBedikaFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: "array" });
      const sheetName =
        wb.SheetNames.find((n) => n.includes("עלות")) || wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      // Find 'שם העובד' column by header name, fallback to col B (index 1)
      const headerRow = rows[0] || [];
      const nameColIdx = headerRow.findIndex(
        (h) => String(h).trim() === "שם העובד" || String(h).trim() === "שם",
      );
      const keyCol = nameColIdx >= 0 ? nameColIdx : 1;
      // Convert 0-based index to Excel column letter
      const toColLetter = (idx) => String.fromCharCode(65 + idx);
      // Detect michpal/employee-number column; fallback to col A (index 0)
      const numColHeaders = [
        "מספר עובד",
        "מס' עובד",
        "מס עובד",
        "מיכפל",
        "מספר מיכפל",
        "ת.ז",
        "תז",
        "תעודת זהות",
        "id",
        "employee id",
        "employee number",
      ];
      const numColIdx = headerRow.findIndex((h) =>
        numColHeaders.some(
          (n) => String(h).trim().toLowerCase() === n.toLowerCase(),
        ),
      );
      const numCol = numColIdx >= 0 ? numColIdx : 0;
      // Value: col K = index 10 from A (VLOOKUP $B$2:$K$295, col 10)
      const lookup = {};
      const rawRows = [];
      rows.slice(1).forEach((row) => {
        const key = String(row[keyCol] || "").trim();
        const num = String(row[numCol] || "").trim();
        const val = parseFloat(row[10]) || 0;
        if (key) {
          lookup[key] = val;
          lookup[key.toLowerCase()] = val;
        }
        if (num) {
          lookup[num] = val;
        }
        if (key || num)
          rawRows.push({ name: key.toLowerCase(), num, value: val });
      });
      const entryCount = rawRows.length;
      setBedikaLookup(lookup);
      setBedikaRows(rawRows);
      setBedikaFileUploaded(true);
      setBedikaSourceWb(wb);
      setBedikaFileInfo({
        found: nameColIdx >= 0,
        colLetter: toColLetter(keyCol),
        headerName: nameColIdx >= 0 ? String(headerRow[nameColIdx]) : null,
        count: entryCount,
        allHeaders: headerRow
          .slice(0, 14)
          .map((h, i) => `${toColLetter(i)}: ${h}`)
          .join(" | "),
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const isIsraeli = (emp) =>
    /^\d{9}$/.test(String(emp.employee_number || "").trim());

  const getEmploymentCost = (emp) => {
    if (costOverrides[emp.employee_id] !== undefined)
      return parseFloat(costOverrides[emp.employee_id]) || 0;
    if (!bedikaFileUploaded) return 0;
    const dbName = String(emp.employee_name || "").trim();
    const dbNum = String(emp.employee_number || "").trim();
    const dbNameLc = dbName.toLowerCase();
    // 1. Exact name match
    if (bedikaLookup[dbName] !== undefined) return bedikaLookup[dbName];
    if (bedikaLookup[dbNameLc] !== undefined) return bedikaLookup[dbNameLc];
    // 2. Exact michpal/number match
    if (dbNum && bedikaLookup[dbNum] !== undefined) return bedikaLookup[dbNum];
    // 3. Contains match: DB name contained in Excel name OR Excel name in DB name
    const byName = bedikaRows.find(
      (r) =>
        r.name &&
        dbNameLc &&
        (r.name.includes(dbNameLc) || dbNameLc.includes(r.name)),
    );
    if (byName) return byName.value;
    // 4. Michpal number match from rows
    if (dbNum) {
      const byNum = bedikaRows.find((r) => r.num === dbNum);
      if (byNum) return byNum.value;
    }
    return 0;
  };

  const getSocialFactor = (emp) => {
    if (socialFactors[emp.employee_id] !== undefined)
      return parseFloat(socialFactors[emp.employee_id]);
    const t = emp.employee_type || "";
    if (t === "קבלן") return parseFloat(globalSocialFactors.kablan) || 1.0;
    return isIsraeli(emp)
      ? parseFloat(globalSocialFactors.chevra_israeli) || 1.11
      : parseFloat(globalSocialFactors.chevra_foreign) || 1.27;
  };

  const exportBedikaToExcel = () => {
    const wb = XLSX.utils.book_new();

    // A:שם  B:מיכפל  C:חול  D:שבת  E:ת׳חול  F:ת׳שבת  G:סה״כ שע׳
    // H:לתשלום  I:הערות  J:עלות העסקה(vlookup)  K:סוצ%
    // L:עלות+סוצ  M:עלות/שעה  N:ת׳שעה לקוח  O:ת׳שבת לקוח
    // P:התקבל  Q:רווח/הפסד
    const HEADERS = [
      "שם",
      "מספר מיכפל",
      'סה"כ חול',
      'סה"כ שבת',
      "תעריף חול",
      "תעריף שבת",
      "סך הכל שעות",
      "לתשלום",
      "הערות",
      "עלות העסקה",
      "סוצ %",
      "עלות כולל סוצ",
      "עלות לשעה",
      "תעריף שעה לקוח",
      "תעריף שבת לקוח",
      "התקבל מהלקוח",
      "רווח/הפסד משוער",
    ];

    // dataStartRow: 1-based Excel row of the first employee data row
    const buildDataRows = (customer, employees, dataStartRow) => {
      const rRate = parseFloat(customerData[customer.id]?.regular_rate) || 0;
      const wRate = parseFloat(customerData[customer.id]?.weekend_rate) || 0;
      const rows = employees.map((emp, i) => {
        const r = dataStartRow + i;
        return [
          emp.employee_name,
          emp.employee_number || emp.michpal_id || "",
          emp.regular_hours,
          emp.weekend_hours,
          emp.hour_rate,
          emp.weekend_hour_rate,
          emp.total_hours,
          emp.to_pay,
          emp.notes,
          costOverrides[emp.employee_id] !== undefined
            ? parseFloat(costOverrides[emp.employee_id]) || 0
            : {
                f: `IFERROR(VLOOKUP(A${r},'עלות העסקה'!$B$2:$K$295,10,FALSE),0)`,
              },
          getSocialFactor(emp),
          { f: `J${r}*K${r}` },
          { f: `IF(G${r}>0,J${r}/G${r},0)` },
          rRate,
          wRate,
          { f: `N${r}*C${r}+O${r}*D${r}` },
          { f: `P${r}-L${r}` },
        ];
      });
      const s = dataStartRow;
      const e = dataStartRow + employees.length - 1;
      const totals = [
        'סה"כ',
        "",
        { f: `SUM(C${s}:C${e})` },
        { f: `SUM(D${s}:D${e})` },
        "",
        "",
        { f: `SUM(G${s}:G${e})` },
        { f: `SUM(H${s}:H${e})` },
        "",
        { f: `SUM(J${s}:J${e})` },
        "",
        { f: `SUM(L${s}:L${e})` },
        "",
        rRate,
        wRate,
        { f: `SUM(P${s}:P${e})` },
        { f: `SUM(Q${s}:Q${e})` },
      ];
      return [...rows, totals];
    };

    const chevraEmps = (id) =>
      (employeeHours[id] || []).filter((e) => e.employee_type === "חברה");
    const large = customers.filter((c) => chevraEmps(c.id).length > 3);
    const small = customers.filter((c) => {
      const l = chevraEmps(c.id).length;
      return l > 0 && l <= 3;
    });

    // Large sites — one sheet each; headers on row 1, data starts at row 2
    large.forEach((c) => {
      const emps = chevraEmps(c.id);
      if (!emps.length) return;
      const sheetData = [HEADERS, ...buildDataRows(c, emps, 2)];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      const sheetName =
        `${c.name}${c.site_name ? "-" + c.site_name : ""}`.substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Small sites — one combined sheet, tracking exact Excel row numbers for formulas
    if (small.length > 0) {
      const allRows = [];
      let excelRow = 1;
      small.forEach((c) => {
        const emps = chevraEmps(c.id);
        allRows.push([
          `${c.name}${c.site_name ? " - " + c.site_name : ""}`,
          ...Array(HEADERS.length - 1).fill(""),
        ]);
        excelRow++;
        allRows.push(HEADERS);
        excelRow++;
        buildDataRows(c, emps, excelRow).forEach((r) => {
          allRows.push(r);
          excelRow++;
        });
        allRows.push(Array(HEADERS.length).fill(""));
        excelRow++;
      });
      const ws = XLSX.utils.aoa_to_sheet(allRows);
      XLSX.utils.book_append_sheet(wb, ws, "אתרים קטנים");
    }

    // Append the original uploaded עלות העסקה sheet so VLOOKUP formulas resolve
    if (bedikaSourceWb) {
      const srcName =
        bedikaSourceWb.SheetNames.find((n) => n.includes("עלות")) ||
        bedikaSourceWb.SheetNames[0];
      if (srcName) {
        XLSX.utils.book_append_sheet(
          wb,
          bedikaSourceWb.Sheets[srcName],
          "עלות העסקה",
        );
      }
    }

    XLSX.writeFile(wb, `בדיקה_${selectedMonth}.xlsx`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Password Gate
  if (!isUnlocked) {
    return (
      <div className='profit-container'>
        <div className='profit-password-gate'>
          <div className='profit-lock-icon'>
            <Lock size={64} />
          </div>
          <h1>דף מוגן</h1>
          <p>הזן סיסמה כדי לגשת לדף חישוב הרווח</p>
          <form
            onSubmit={handlePasswordSubmit}
            className='profit-password-form'
          >
            <div className='profit-password-input-wrapper'>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder='הזן סיסמה'
                autoFocus
              />
              <button
                type='button'
                className='profit-toggle-password'
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordError && (
              <p className='profit-error-message'>{passwordError}</p>
            )}
            <button type='submit' className='profit-unlock-btn'>
              <Unlock size={18} />
              כניסה
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── בדיקה mode selection screen ───
  if (isBedika === null) {
    return (
      <div className='profit-container'>
        <div className='profit-password-gate'>
          <div className='profit-lock-icon'>
            <Calculator size={64} />
          </div>
          <h1>חישוב רווח</h1>
          <p style={{ marginBottom: "8px" }}>בחר סוג תצוגה</p>
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginTop: "24px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              className='profit-unlock-btn'
              onClick={() => {
                setIsBedika(true);
                loadEmployeeHours();
              }}
            >
              בדיקה
            </button>
            <button
              className='profit-unlock-btn'
              style={{ background: "#6b7280" }}
              onClick={() => setIsBedika(false)}
            >
              חישוב רווח רגיל
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── filter helper ───
  const filterEmps = (emps, customerId) => {
    return emps.filter((e) => {
      if (filters.name && !e.employee_name.includes(filters.name)) return false;
      if (filters.type && e.employee_type !== filters.type) return false;
      if (filters.israeli === "yes" && !isIsraeli(e)) return false;
      if (filters.israeli === "no" && isIsraeli(e)) return false;
      if (filters.site) {
        const c = customers.find((c) => c.id === customerId);
        const siteName = (c?.site_name || c?.name || "").toLowerCase();
        if (!siteName.includes(filters.site.toLowerCase())) return false;
      }
      return true;
    });
  };

  // ─── בדיקה full view ───
  if (isBedika) {
    const allEmpsForSite = (id) => employeeHours[id] || [];
    const largeSites = customers.filter((c) => allEmpsForSite(c.id).length > 3);
    const smallSites = customers.filter((c) => {
      const l = allEmpsForSite(c.id).length;
      return l > 0 && l <= 3;
    });

    const renderSiteTable = (customer) => {
      const allEmps = employeeHours[customer.id] || [];
      const emps = filterEmps(allEmps, customer.id);
      const rRate = parseFloat(customerData[customer.id]?.regular_rate) || 0;
      const wRate = parseFloat(customerData[customer.id]?.weekend_rate) || 0;
      return (
        <div key={customer.id} style={{ marginBottom: "32px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
              flexWrap: "wrap",
            }}
          >
            <h3 className='bedika-site-title'>
              {customer.name}
              {customer.site_name ? ` — ${customer.site_name}` : ""}
              <span className='bedika-site-count'>
                ({allEmps.length} עובדים
                {emps.length !== allEmps.length
                  ? `, ${emps.length} מסוננים`
                  : ""}
                )
              </span>
            </h3>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "12px", color: "#6b7280" }}>
                ת׳ שעה לקוח:
              </label>
              <input
                type='number'
                style={{
                  width: "80px",
                  padding: "4px 6px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "13px",
                }}
                value={customerData[customer.id]?.regular_rate || ""}
                placeholder='0'
                onChange={(e) =>
                  handleInputChange(customer.id, "regular_rate", e.target.value)
                }
              />
              <label style={{ fontSize: "12px", color: "#6b7280" }}>
                ת׳ שבת לקוח:
              </label>
              <input
                type='number'
                style={{
                  width: "80px",
                  padding: "4px 6px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "13px",
                }}
                value={customerData[customer.id]?.weekend_rate || ""}
                placeholder='0'
                onChange={(e) =>
                  handleInputChange(customer.id, "weekend_rate", e.target.value)
                }
              />
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className='bedika-table'>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>סוג</th>
                  <th>מיכפל</th>
                  <th>חול</th>
                  <th>שבת</th>
                  <th>ת׳ חול</th>
                  <th>ת׳ שבת</th>
                  <th>סה״כ שע׳</th>
                  <th>לתשלום</th>
                  <th>הערות</th>
                  <th>עלות העסקה</th>
                  <th>סוצ %</th>
                  <th>עלות+סוצ</th>
                  <th>עלות/שעה</th>
                  <th>ת׳ שעה לקוח</th>
                  <th>ת׳ שבת לקוח</th>
                  <th>התקבל</th>
                  <th>רווח/הפסד</th>
                </tr>
              </thead>
              <tbody>
                {emps.map((emp) => {
                  const cost = getEmploymentCost(emp);
                  const israeli = isIsraeli(emp);
                  const factor = getSocialFactor(emp);
                  const social = cost * factor;
                  const hCost =
                    emp.total_hours > 0 ? cost / emp.total_hours : 0;
                  const recv =
                    rRate * emp.regular_hours + wRate * emp.weekend_hours;
                  const profit = recv - social;
                  const isDual = emp.employee_type === "קבלן ו חברה";
                  const typeBadgeColor =
                    emp.employee_type === "חברה"
                      ? "#0ea5e9"
                      : emp.employee_type === "קבלן"
                        ? "#6366f1"
                        : "#f59e0b";
                  return (
                    <tr key={emp.employee_id}>
                      <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                        {israeli && (
                          <span
                            style={{ fontSize: "11px", marginLeft: "3px" }}
                            title='ישראלי'
                          >
                            🇮🇱
                          </span>
                        )}
                        {emp.employee_name}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            background: typeBadgeColor + "33",
                            color: typeBadgeColor,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {emp.employee_type || "קבלן"}
                        </span>
                      </td>
                      <td style={{ fontSize: "11px", color: "#94a3b8" }}>
                        {emp.employee_number || "—"}
                      </td>
                      <td>{emp.regular_hours.toFixed(1)}</td>
                      <td>{emp.weekend_hours.toFixed(1)}</td>
                      <td>{emp.hour_rate.toFixed(2)}</td>
                      <td>{emp.weekend_hour_rate.toFixed(2)}</td>
                      <td>
                        <b>{emp.total_hours.toFixed(1)}</b>
                      </td>
                      <td>
                        {isDual ? (
                          <span style={{ fontSize: "11px" }}>
                            <span style={{ color: "#6366f1" }}>
                              ק: {formatCurrency(emp.kablan_to_pay || 0)}
                            </span>
                            <br />
                            <span style={{ color: "#0ea5e9" }}>
                              ח: {formatCurrency(emp.company_to_pay || 0)}
                            </span>
                          </span>
                        ) : (
                          formatCurrency(emp.to_pay)
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: "120px",
                          fontSize: "11px",
                          color: "#6b7280",
                        }}
                      >
                        {emp.notes}
                      </td>
                      <td style={{ color: "#0ea5e9", padding: "2px 4px" }}>
                        <input
                          type='number'
                          step='1'
                          style={{
                            width: "90px",
                            padding: "2px 6px",
                            border: "1px solid #0ea5e9",
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: "#0ea5e9",
                            background: "transparent",
                            textAlign: "center",
                          }}
                          value={
                            costOverrides[emp.employee_id] !== undefined
                              ? costOverrides[emp.employee_id]
                              : cost
                          }
                          onChange={(ev) =>
                            setCostOverrides((p) => ({
                              ...p,
                              [emp.employee_id]: ev.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          style={{
                            width: "60px",
                            padding: "2px 4px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "12px",
                            textAlign: "center",
                            background: "transparent",
                            color: "#e2e8f0",
                          }}
                          value={socialFactors[emp.employee_id] ?? factor}
                          onChange={(e) =>
                            setSocialFactors((p) => ({
                              ...p,
                              [emp.employee_id]: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td style={{ color: "#f59e0b" }}>
                        {formatCurrency(social)}
                      </td>
                      <td>{hCost > 0 ? formatCurrency(hCost) : "—"}</td>
                      <td>{formatCurrency(rRate)}</td>
                      <td>{formatCurrency(wRate)}</td>
                      <td style={{ color: "#10b981", fontWeight: 600 }}>
                        {formatCurrency(recv)}
                      </td>
                      <td
                        style={{
                          color: profit >= 0 ? "#10b981" : "#ef4444",
                          fontWeight: 700,
                        }}
                      >
                        {formatCurrency(profit)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td>סה״כ</td>
                  <td />
                  <td />
                  <td>
                    {emps.reduce((s, e) => s + e.regular_hours, 0).toFixed(1)}
                  </td>
                  <td>
                    {emps.reduce((s, e) => s + e.weekend_hours, 0).toFixed(1)}
                  </td>
                  <td />
                  <td />
                  <td>
                    {emps.reduce((s, e) => s + e.total_hours, 0).toFixed(1)}
                  </td>
                  <td>
                    {formatCurrency(emps.reduce((s, e) => s + e.to_pay, 0))}
                  </td>
                  <td />
                  <td>
                    {formatCurrency(
                      emps.reduce((s, e) => s + getEmploymentCost(e), 0),
                    )}
                  </td>
                  <td />
                  <td>
                    {formatCurrency(
                      emps.reduce(
                        (s, e) => s + getEmploymentCost(e) * getSocialFactor(e),
                        0,
                      ),
                    )}
                  </td>
                  <td />
                  <td />
                  <td />
                  <td style={{ color: "#10b981" }}>
                    {formatCurrency(
                      emps.reduce(
                        (s, e) =>
                          s + rRate * e.regular_hours + wRate * e.weekend_hours,
                        0,
                      ),
                    )}
                  </td>
                  <td
                    style={{
                      color:
                        emps.reduce(
                          (s, e) =>
                            s +
                            (rRate * e.regular_hours +
                              wRate * e.weekend_hours) -
                            getEmploymentCost(e) * getSocialFactor(e),
                          0,
                        ) >= 0
                          ? "#10b981"
                          : "#ef4444",
                    }}
                  >
                    {formatCurrency(
                      emps.reduce(
                        (s, e) =>
                          s +
                          (rRate * e.regular_hours + wRate * e.weekend_hours) -
                          getEmploymentCost(e) * getSocialFactor(e),
                        0,
                      ),
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className='profit-container'>
        {/* Header */}
        <div className='profit-header'>
          <div className='profit-header-content'>
            <h1>
              <Calculator className='profit-header-icon' />
              בדיקה — {formatMonth(selectedMonth)}
            </h1>
          </div>
          <div className='profit-header-actions'>
            <div className='profit-month-selector'>
              <Calendar size={18} />
              <input
                type='month'
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <label
                className='profit-calculate-btn'
                style={{ cursor: "pointer", background: "#6366f1" }}
              >
                <input
                  type='file'
                  accept='.xlsx,.xls'
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={(e) => {
                    if (e.target.files[0]) parseBedikaFile(e.target.files[0]);
                  }}
                />
                📂{" "}
                {bedikaFileUploaded ? "✓ עלות העסקה נטענה" : "העלה עלות העסקה"}
              </label>
              <button
                className='profit-calculate-btn'
                onClick={saveAllProfitData}
                disabled={saving}
                style={{ background: "#059669" }}
              >
                <Calculator size={16} /> {saving ? "שומר..." : "שמור תעריפים"}
              </button>
              <button
                className='profit-calculate-btn'
                onClick={exportBedikaToExcel}
                style={{ background: "#0ea5e9" }}
              >
                📊 יצא לאקסל
              </button>
              <button
                className='profit-calculate-btn'
                style={{ background: "#6b7280" }}
                onClick={() => {
                  setIsBedika(null);
                }}
              >
                חזרה
              </button>
            </div>
          </div>
        </div>

        {/* File parse info panel */}
        {bedikaFileInfo && (
          <div
            style={{
              margin: "12px 0",
              padding: "12px 16px",
              borderRadius: "8px",
              background: bedikaFileInfo.found
                ? "rgba(16,185,129,0.12)"
                : "rgba(239,68,68,0.12)",
              border: `1px solid ${bedikaFileInfo.found ? "#10b981" : "#ef4444"}`,
              fontSize: "12px",
              color: "#e2e8f0",
              direction: "rtl",
            }}
          >
            {bedikaFileInfo.found ? (
              <span>
                ✅ נמצאה עמודה <b>"{bedikaFileInfo.headerName}"</b> בעמודה{" "}
                <b>{bedikaFileInfo.colLetter}</b> — נטענו{" "}
                <b>{bedikaFileInfo.count}</b> שורות
              </span>
            ) : (
              <span>
                ⚠️ לא נמצאה עמודה "שם העובד" — נעשה שימוש בעמודה{" "}
                <b>{bedikaFileInfo.colLetter}</b> (fallback) — נטענו{" "}
                <b>{bedikaFileInfo.count}</b> שורות
              </span>
            )}
            <div
              style={{
                marginTop: "6px",
                color: "#94a3b8",
                fontSize: "11px",
                wordBreak: "break-all",
              }}
            >
              <b>כותרות שנמצאו:</b> {bedikaFileInfo.allHeaders}
            </div>
          </div>
        )}

        {/* ── Global social factors panel ── */}
        <div
          style={{
            display: "flex",
            gap: "24px",
            flexWrap: "wrap",
            margin: "12px 0",
            padding: "12px 16px",
            background: "rgba(99,102,241,0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(99,102,241,0.3)",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#e2e8f0",
              whiteSpace: "nowrap",
            }}
          >
            מקדמים סוציאליים גלובליים:
          </span>
          {[
            { key: "chevra_israeli", label: "חברה ישראלי" },
            { key: "chevra_foreign", label: "חברה לא ישראלי" },
            { key: "kablan", label: "קבלן" },
          ].map(({ key, label }) => (
            <label
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "#cbd5e1",
              }}
            >
              {label}:
              <input
                type='number'
                step='0.01'
                min='1'
                max='3'
                style={{
                  width: "70px",
                  padding: "3px 6px",
                  borderRadius: "6px",
                  border: "1px solid rgba(99,102,241,0.5)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#e2e8f0",
                  fontSize: "13px",
                  textAlign: "center",
                }}
                value={globalSocialFactors[key]}
                onChange={(e) =>
                  setGlobalSocialFactors((p) => ({
                    ...p,
                    [key]: e.target.value,
                  }))
                }
              />
            </label>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            margin: "8px 0 16px",
            alignItems: "center",
          }}
        >
          <input
            type='text'
            placeholder='🔍 חיפוש שם עובד'
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.3)",
              color: "#e2e8f0",
              fontSize: "13px",
              minWidth: "160px",
            }}
            value={filters.name}
            onChange={(e) =>
              setFilters((f) => ({ ...f, name: e.target.value }))
            }
          />
          <select
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.5)",
              color: "#e2e8f0",
              fontSize: "13px",
            }}
            value={filters.type}
            onChange={(e) =>
              setFilters((f) => ({ ...f, type: e.target.value }))
            }
          >
            <option value=''>כל הסוגים</option>
            <option value='חברה'>חברה</option>
            <option value='קבלן'>קבלן</option>
            <option value='קבלן ו חברה'>קבלן ו חברה</option>
          </select>
          <select
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.5)",
              color: "#e2e8f0",
              fontSize: "13px",
            }}
            value={filters.israeli}
            onChange={(e) =>
              setFilters((f) => ({ ...f, israeli: e.target.value }))
            }
          >
            <option value=''>כל העובדים</option>
            <option value='yes'>ישראלים 🇮🇱</option>
            <option value='no'>לא ישראלים</option>
          </select>
          <input
            type='text'
            placeholder='🏢 אתר / לקוח'
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.3)",
              color: "#e2e8f0",
              fontSize: "13px",
              minWidth: "140px",
            }}
            value={filters.site}
            onChange={(e) =>
              setFilters((f) => ({ ...f, site: e.target.value }))
            }
          />
          {(filters.name ||
            filters.type ||
            filters.israeli ||
            filters.site) && (
            <button
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: "none",
                background: "#ef4444",
                color: "#fff",
                fontSize: "12px",
                cursor: "pointer",
              }}
              onClick={() =>
                setFilters({ name: "", type: "", israeli: "", site: "" })
              }
            >
              נקה סינון ✕
            </button>
          )}
        </div>

        {bedikaLoading && (
          <div className='profit-loading-state'>
            <div className='profit-spinner' />
            <p>טוען נתוני עובדים...</p>
          </div>
        )}

        {!bedikaLoading && (
          <>
            {/* Large sites — one table each */}
            {largeSites.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h2
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    marginBottom: "12px",
                    borderBottom: "2px solid #e5e7eb",
                    paddingBottom: "8px",
                  }}
                >
                  אתרים גדולים
                </h2>
                {largeSites.map((c) => renderSiteTable(c))}
              </div>
            )}

            {/* Small sites — one combined table */}
            {smallSites.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    marginBottom: "12px",
                    borderBottom: "2px solid #e5e7eb",
                    paddingBottom: "8px",
                  }}
                >
                  אתרים קטנים (עד 3 עובדים)
                </h2>
                {smallSites.map((c) => renderSiteTable(c))}
              </div>
            )}

            {largeSites.length === 0 &&
              smallSites.length === 0 &&
              !bedikaLoading &&
              (() => {
                const allEmps = Object.values(employeeHours).flat();
                const chevraCount = allEmps.filter(
                  (e) => e.employee_type === "חברה",
                ).length;
                const types = [
                  ...new Set(allEmps.map((e) => e.employee_type)),
                ].join(", ");
                const custIds = Object.keys(employeeHours).join(", ");
                return (
                  <div
                    className='profit-loading-state'
                    style={{ textAlign: "right", direction: "rtl" }}
                  >
                    {allEmps.length === 0 ? (
                      <p>
                        ⚠️ אין נתוני נוכחות לחודש <b>{selectedMonth}</b> בכלל
                      </p>
                    ) : (
                      <>
                        <p>
                          ⚠️ נמצאו <b>{allEmps.length}</b> עובדים בסה״כ לחודש{" "}
                          <b>{selectedMonth}</b>, אך <b>0</b> מסוג חברה
                        </p>
                        <p style={{ fontSize: "12px", color: "#94a3b8" }}>
                          סוגי עובדים שנמצאו: <b>{types || "לא ידוע"}</b>
                        </p>
                        <p style={{ fontSize: "12px", color: "#94a3b8" }}>
                          מספרי לקוחות ב-API: <b>{custIds}</b>
                        </p>
                      </>
                    )}
                  </div>
                );
              })()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className='profit-container'>
      <div className='profit-header'>
        <div className='profit-header-content'>
          <h1>
            <Calculator className='profit-header-icon' />
            חישוב רווח
          </h1>
          <p className='profit-header-subtitle'>
            חישוב רווח לפי לקוח/אתר - הכנסות פחות משכורות ואכיפה
          </p>
        </div>

        <div className='profit-header-actions'>
          <div className='profit-month-selector'>
            <Calendar size={18} />
            <input
              type='month'
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>

          <div className='profit-view-toggle'>
            <button
              className={viewMode === "sites" ? "active" : ""}
              onClick={() => setViewMode("sites")}
            >
              <Building2 size={16} />
              אתרים
            </button>
            <button
              className={viewMode === "customers" ? "active" : ""}
              onClick={() => setViewMode("customers")}
            >
              <Users size={16} />
              לקוחות
            </button>
          </div>

          <button
            className='profit-calculate-btn'
            onClick={saveAllProfitData}
            disabled={saving}
          >
            <Calculator size={18} />
            {saving ? "שומר..." : "שמור נתונים"}
          </button>
        </div>
      </div>

      {/* Results Summary - Calculate totals from presence hours and costs */}
      {Object.keys(presenceHours).length > 0 && (
        <div className='profit-results-summary'>
          <div className='profit-summary-card hours'>
            <Clock size={24} />
            <div className='profit-summary-card-content'>
              <span className='profit-label'>סה״כ שעות</span>
              <span className='profit-value'>
                {Object.values(presenceHours)
                  .reduce((sum, h) => sum + (h.total_hours || 0), 0)
                  .toFixed(1)}
              </span>
              <span
                className='profit-label'
                style={{ fontSize: "11px", marginTop: "4px" }}
              >
                קבלן:{" "}
                {Object.values(presenceHours)
                  .reduce((s, h) => s + (h.kablan_hours || 0), 0)
                  .toFixed(1)}
                {" | "}
                חברה:{" "}
                {Object.values(presenceHours)
                  .reduce((s, h) => s + (h.company_hours || 0), 0)
                  .toFixed(1)}
              </span>
            </div>
          </div>
          <div className='profit-summary-card revenue'>
            <TrendingUp size={24} />
            <div className='profit-summary-card-content'>
              <span className='profit-label'>סה״כ הכנסות (משוער)</span>
              <span className='profit-value'>
                {formatCurrency(
                  customers.reduce((sum, c) => {
                    const hours = presenceHours[c.id] || {};
                    const regularRate =
                      parseFloat(customerData[c.id]?.regular_rate) || 0;
                    const weekendRate =
                      parseFloat(customerData[c.id]?.weekend_rate) || 0;
                    return (
                      sum +
                      (hours.regular_hours || 0) * regularRate +
                      (hours.weekend_hours || 0) * weekendRate
                    );
                  }, 0),
                )}
              </span>
            </div>
          </div>
          <div className='profit-summary-card expenses'>
            <TrendingDown size={24} />
            <div className='profit-summary-card-content'>
              <span className='profit-label'>סה״כ משכורות</span>
              <span className='profit-value'>
                {formatCurrency(
                  Object.values(systemCosts).reduce(
                    (sum, c) => sum + (c.salaries || 0),
                    0,
                  ),
                )}
              </span>
            </div>
          </div>
          <div className='profit-summary-card enforcement'>
            <Shield size={24} />
            <div className='profit-summary-card-content'>
              <span className='profit-label'>סה״כ תגבור</span>
              <span className='profit-value'>
                {formatCurrency(
                  Object.values(systemCosts).reduce(
                    (sum, c) => sum + (c.enforcement || 0),
                    0,
                  ),
                )}
              </span>
            </div>
          </div>
          <div
            className={`profit-summary-card profit-total ${(() => {
              const totalRevenue = customers.reduce((sum, c) => {
                const hours = presenceHours[c.id] || {};
                const regularRate =
                  parseFloat(customerData[c.id]?.regular_rate) || 0;
                const weekendRate =
                  parseFloat(customerData[c.id]?.weekend_rate) || 0;
                return (
                  sum +
                  (hours.regular_hours || 0) * regularRate +
                  (hours.weekend_hours || 0) * weekendRate
                );
              }, 0);
              const totalSalaries = Object.values(systemCosts).reduce(
                (sum, c) => sum + (c.salaries || 0),
                0,
              );
              const totalEnforcement = Object.values(systemCosts).reduce(
                (sum, c) => sum + (c.enforcement || 0),
                0,
              );
              return totalRevenue - totalSalaries - totalEnforcement >= 0
                ? "positive"
                : "negative";
            })()}`}
          >
            <DollarSign size={24} />
            <div className='profit-summary-card-content'>
              <span className='profit-label'>סה״כ רווח (משוער)</span>
              <span className='profit-value'>
                {formatCurrency(
                  (() => {
                    const totalRevenue = customers.reduce((sum, c) => {
                      const hours = presenceHours[c.id] || {};
                      const regularRate =
                        parseFloat(customerData[c.id]?.regular_rate) || 0;
                      const weekendRate =
                        parseFloat(customerData[c.id]?.weekend_rate) || 0;
                      return (
                        sum +
                        (hours.regular_hours || 0) * regularRate +
                        (hours.weekend_hours || 0) * weekendRate
                      );
                    }, 0);
                    const totalSalaries = Object.values(systemCosts).reduce(
                      (sum, c) => sum + (c.salaries || 0),
                      0,
                    );
                    const totalEnforcement = Object.values(systemCosts).reduce(
                      (sum, c) => sum + (c.enforcement || 0),
                      0,
                    );
                    return totalRevenue - totalSalaries - totalEnforcement;
                  })(),
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Customer/Site Cards */}
      {loading ? (
        <div className='profit-loading-state'>
          <div className='profit-spinner'></div>
          <p>טוען לקוחות...</p>
        </div>
      ) : viewMode === "sites" ? (
        <div className='profit-customers-grid'>
          {customers.map((customer) => {
            const hours = presenceHours[customer.id] || {};
            const costs = systemCosts[customer.id] || {};
            const saved = savedProfitData[customer.id] || {};
            const regularRate =
              parseFloat(customerData[customer.id]?.regular_rate) || 0;
            const weekendRate =
              parseFloat(customerData[customer.id]?.weekend_rate) || 0;
            const regularHours = hours.regular_hours || 0;
            const weekendHours = hours.weekend_hours || 0;
            const revenue =
              regularHours * regularRate + weekendHours * weekendRate;
            const profit =
              revenue - (costs.salaries || 0) - (costs.enforcement || 0);

            return (
              <div key={customer.id} className='profit-customer-card'>
                <div className='profit-card-header'>
                  <Building2 size={20} />
                  <div className='profit-card-title'>
                    <h3>{customer.name}</h3>
                    {customer.site_name && (
                      <span className='profit-site-name'>
                        {customer.site_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hours Display - Regular & Weekend */}
                <div className='profit-hours-display'>
                  <div className='profit-hours-item'>
                    <span className='profit-hours-label'>שעות רגילות</span>
                    <span className='profit-hours-value'>
                      {regularHours.toFixed(1)}
                    </span>
                  </div>
                  <div className='profit-hours-item'>
                    <span className='profit-hours-label'>שעות שבת</span>
                    <span className='profit-hours-value'>
                      {weekendHours.toFixed(1)}
                    </span>
                  </div>
                  <div className='profit-hours-item total'>
                    <span className='profit-hours-label'>סה״כ שעות</span>
                    <span className='profit-hours-value'>
                      {(hours.total_hours || 0).toFixed(1)}
                    </span>
                  </div>
                </div>
                {/* קבלן / חברה breakdown */}
                {((hours.kablan_hours || 0) > 0 ||
                  (hours.company_hours || 0) > 0) && (
                  <div
                    className='profit-hours-display'
                    style={{
                      marginTop: "6px",
                      background: "rgba(0,0,0,0.04)",
                      borderRadius: "6px",
                      padding: "6px 10px",
                    }}
                  >
                    <div className='profit-hours-item'>
                      <span
                        className='profit-hours-label'
                        style={{ fontSize: "11px" }}
                      >
                        קבלן
                      </span>
                      <span
                        className='profit-hours-value'
                        style={{ fontSize: "13px", color: "#6366f1" }}
                      >
                        {(hours.kablan_hours || 0).toFixed(1)}
                      </span>
                    </div>
                    <div className='profit-hours-item'>
                      <span
                        className='profit-hours-label'
                        style={{ fontSize: "11px" }}
                      >
                        חברה
                      </span>
                      <span
                        className='profit-hours-value'
                        style={{ fontSize: "13px", color: "#0ea5e9" }}
                      >
                        {(hours.company_hours || 0).toFixed(1)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Rate Inputs - Regular & Weekend */}
                <div className='profit-card-inputs'>
                  <div className='profit-input-group'>
                    <label>תעריף רגיל (₪)</label>
                    <input
                      type='number'
                      value={customerData[customer.id]?.regular_rate || ""}
                      onChange={(e) =>
                        handleInputChange(
                          customer.id,
                          "regular_rate",
                          e.target.value,
                        )
                      }
                      placeholder='0'
                      min='0'
                    />
                  </div>
                  <div className='profit-input-group'>
                    <label>תעריף שבת (₪)</label>
                    <input
                      type='number'
                      value={customerData[customer.id]?.weekend_rate || ""}
                      onChange={(e) =>
                        handleInputChange(
                          customer.id,
                          "weekend_rate",
                          e.target.value,
                        )
                      }
                      placeholder='0'
                      min='0'
                    />
                  </div>
                </div>

                {/* Calculated Results */}
                <div className='profit-card-results'>
                  <div className='profit-result-row'>
                    <span>הכנסות:</span>
                    <span className='positive'>{formatCurrency(revenue)}</span>
                  </div>
                  <div className='profit-result-row'>
                    <span>משכורות:</span>
                    <span className='negative'>
                      -{formatCurrency(costs.salaries || 0)}
                    </span>
                  </div>
                  <div className='profit-result-row'>
                    <span>תגבור:</span>
                    <span className='negative'>
                      -{formatCurrency(costs.enforcement || 0)}
                    </span>
                  </div>
                  <div
                    className={`profit-result-row total ${
                      profit >= 0 ? "profit" : "loss"
                    }`}
                  >
                    <span>רווח:</span>
                    <span>{formatCurrency(profit)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className='profit-customers-grid'>
          {uniqueCustomerNames.map((customerName) => {
            // Calculate totals for this customer
            const customerSites = customersByName[customerName];

            // Sum hours from all sites
            const totalRegularHours = customerSites.reduce(
              (sum, s) => sum + (presenceHours[s.id]?.regular_hours || 0),
              0,
            );
            const totalWeekendHours = customerSites.reduce(
              (sum, s) => sum + (presenceHours[s.id]?.weekend_hours || 0),
              0,
            );
            const totalHours = customerSites.reduce(
              (sum, s) => sum + (presenceHours[s.id]?.total_hours || 0),
              0,
            );

            // Sum costs from all sites
            const totalSalaries = customerSites.reduce(
              (sum, s) => sum + (systemCosts[s.id]?.salaries || 0),
              0,
            );
            const totalEnforcement = customerSites.reduce(
              (sum, s) => sum + (systemCosts[s.id]?.enforcement || 0),
              0,
            );

            const regularRate =
              parseFloat(customerLevelData[customerName]?.regular_rate) || 0;
            const weekendRate =
              parseFloat(customerLevelData[customerName]?.weekend_rate) || 0;
            const revenue =
              totalRegularHours * regularRate + totalWeekendHours * weekendRate;
            const profit = revenue - totalSalaries - totalEnforcement;

            return (
              <div key={customerName} className='profit-customer-card'>
                <div className='profit-card-header'>
                  <Users size={20} />
                  <div className='profit-card-title'>
                    <h3>{customerName}</h3>
                    <span className='profit-site-name'>
                      {customerSites.length} אתרים
                    </span>
                  </div>
                </div>

                {/* Hours Display - Regular & Weekend */}
                <div className='profit-hours-display'>
                  <div className='profit-hours-item'>
                    <span className='profit-hours-label'>שעות רגילות</span>
                    <span className='profit-hours-value'>
                      {totalRegularHours.toFixed(1)}
                    </span>
                  </div>
                  <div className='profit-hours-item'>
                    <span className='profit-hours-label'>שעות שבת</span>
                    <span className='profit-hours-value'>
                      {totalWeekendHours.toFixed(1)}
                    </span>
                  </div>
                  <div className='profit-hours-item total'>
                    <span className='profit-hours-label'>סה״כ שעות</span>
                    <span className='profit-hours-value'>
                      {totalHours.toFixed(1)}
                    </span>
                  </div>
                </div>
                {/* קבלן / חברה breakdown */}
                {(() => {
                  const kablan = customerSites.reduce(
                    (s, site) =>
                      s + (presenceHours[site.id]?.kablan_hours || 0),
                    0,
                  );
                  const company = customerSites.reduce(
                    (s, site) =>
                      s + (presenceHours[site.id]?.company_hours || 0),
                    0,
                  );
                  if (kablan === 0 && company === 0) return null;
                  return (
                    <div
                      className='profit-hours-display'
                      style={{
                        marginTop: "6px",
                        background: "rgba(0,0,0,0.04)",
                        borderRadius: "6px",
                        padding: "6px 10px",
                      }}
                    >
                      <div className='profit-hours-item'>
                        <span
                          className='profit-hours-label'
                          style={{ fontSize: "11px" }}
                        >
                          קבלן
                        </span>
                        <span
                          className='profit-hours-value'
                          style={{ fontSize: "13px", color: "#6366f1" }}
                        >
                          {kablan.toFixed(1)}
                        </span>
                      </div>
                      <div className='profit-hours-item'>
                        <span
                          className='profit-hours-label'
                          style={{ fontSize: "11px" }}
                        >
                          חברה
                        </span>
                        <span
                          className='profit-hours-value'
                          style={{ fontSize: "13px", color: "#0ea5e9" }}
                        >
                          {company.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Rate Inputs - Regular & Weekend */}
                <div className='profit-card-inputs'>
                  <div className='profit-input-group'>
                    <label>תעריף רגיל (₪)</label>
                    <input
                      type='number'
                      value={
                        customerLevelData[customerName]?.regular_rate || ""
                      }
                      onChange={(e) =>
                        handleCustomerLevelChange(
                          customerName,
                          "regular_rate",
                          e.target.value,
                        )
                      }
                      placeholder='0'
                      min='0'
                    />
                  </div>
                  <div className='profit-input-group'>
                    <label>תעריף שבת (₪)</label>
                    <input
                      type='number'
                      value={
                        customerLevelData[customerName]?.weekend_rate || ""
                      }
                      onChange={(e) =>
                        handleCustomerLevelChange(
                          customerName,
                          "weekend_rate",
                          e.target.value,
                        )
                      }
                      placeholder='0'
                      min='0'
                    />
                  </div>
                </div>

                {/* Calculated Results */}
                <div className='profit-card-results'>
                  <div className='profit-result-row'>
                    <span>הכנסות:</span>
                    <span className='positive'>{formatCurrency(revenue)}</span>
                  </div>
                  <div className='profit-result-row'>
                    <span>משכורות:</span>
                    <span className='negative'>
                      -{formatCurrency(totalSalaries)}
                    </span>
                  </div>
                  <div className='profit-result-row'>
                    <span>תגבור:</span>
                    <span className='negative'>
                      -{formatCurrency(totalEnforcement)}
                    </span>
                  </div>
                  <div
                    className={`profit-result-row total ${
                      profit >= 0 ? "profit" : "loss"
                    }`}
                  >
                    <span>רווח משוער:</span>
                    <span>{formatCurrency(profit)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Profit;
